import React, { type ReactNode } from 'react';
import { t } from 'i18next';

import { ArrowLeftLine } from '@datanomik/core-ui/icons';
import { Button, type ButtonVariant, cn, Dialog, DialogBody, DialogFooter, DialogHeader, DialogPanel, ProgressBar, Tooltip } from '@datanomik/core-ui';

import { useAppContext } from '@/contexts/app';

import './styles.css';
interface ModalProps {
  actionConfirmClassName?: string;
  confirmVariant?: ButtonVariant;
  className?: string;
  children: ReactNode;
  title?: string;
  expand?: boolean;
  openModal: boolean;
  closeModal?: (...args: any[]) => any;
  backModal?: (...args: any[]) => any;
  primaryButton?: string;
  actionConfirm?: (...args: any[]) => any;
  icon?: Record<string, any>;
  backdropClose?: boolean;
  DialogProps?: Record<string, any>;
  DialogHeaderProps?: Record<string, any>;
  DialogActionsProps?: Record<string, any>;
  isLoadingKey?: string;
}

const Modal = ({
  className,
  openModal,
  closeModal,
  title,
  children,
  primaryButton,
  backModal,
  actionConfirm,
  backdropClose = true,
  DialogProps,
  DialogHeaderProps,
  DialogActionsProps,
  isLoadingKey = 'modal',
  actionConfirmClassName = '',
  confirmVariant = 'primary',
}: ModalProps) => {
  const { isLoading } = useAppContext();

  const focusPanelOnOpen = (event) => {
    event.preventDefault();
    event.currentTarget?.focus?.({ preventScroll: true });
  };

  const {
    maxWidth, fullWidth: _fullWidth, PaperProps, scroll: _scroll,
    backdropClose: _dialogBackdropClose, ...restDialogProps
  } = DialogProps ?? {};
  const {
    text, subText, startAdornment, endAdornment, ...restHeaderProps
  } = DialogHeaderProps ?? {};

  return (
    <Dialog
      open={openModal}
      onOpenChange={(next) => {
        if (!next && backdropClose && closeModal) closeModal();
      }}
    >
      <DialogPanel
        className={cn('dn-modal-panel', className)}
        style={PaperProps?.sx}
        size={maxWidth === false ? 'auto' : (maxWidth ?? 'sm')}
        fullWidth={false}
        onOpenAutoFocus={focusPanelOnOpen}
        {...restDialogProps}
      >
        {(DialogHeaderProps || title || backModal || closeModal) && (
          <DialogHeader
            icon={(
              backModal && (
                <Tooltip label={t('Back')}>
                  <Button
                    onClick={closeModal}
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('Back')}
                  >
                    <ArrowLeftLine />
                  </Button>
                </Tooltip>
              )
            )}
            title={text ? t(text) : t(title)}
            description={subText}
            {...(startAdornment ? { icon: startAdornment } : {})}
            {...(endAdornment ? { action: endAdornment } : {})}
            {...restHeaderProps}
          />
        )}

        <DialogBody>
          <ProgressBar size="xs" style={{ opacity: isLoading[isLoadingKey] ? 1 : 0 }} />
          <div data-slot="modal-content" className="flex flex-col gap-5">
            {children}
          </div>
        </DialogBody>

        {(DialogActionsProps?.dialogActions || primaryButton) && (
          <DialogFooter>
            {DialogActionsProps?.dialogActions}
            {primaryButton && (
              <>
                <Button
                  variant="outline"
                  onClick={closeModal}
                  className="flex-1"
                >
                  {t('Cancel')}
                </Button>
                <Button
                  onClick={actionConfirm}
                  variant={confirmVariant}
                  autoFocus
                  className={cn('flex-1', actionConfirmClassName)}
                  disabled={isLoading[isLoadingKey] || !actionConfirm}
                >
                  {t(primaryButton)}
                </Button>
              </>
            )}
          </DialogFooter>
        )}

      </DialogPanel>
    </Dialog>
  );
};
export default Modal;
